/**
 * AGM AFS — 문서별 서명자 JSON (UFS 패턴 + Notice 이사 1명).
 */
import { dedupePresetSigners } from '../../../presetSignerDedup';
import type { AgmAfsFormModel } from './useFormPayload';
import type { AgmEntityPersonRow } from './useFormPayload';
import {
  buildAgmUfsAttendanceSignerVariables,
  buildAgmUfsConsentSignerVariables,
  buildAgmUfsMinutesMultipleChairmanSignerVariables,
  buildAgmUfsMinutesSoleCorporateSignerVariables,
  buildAgmUfsMinutesSoleIndividualSignerVariables,
  collectAgmConsentSignerRows,
  collectAgmShareholderSignerRows,
  dedupeAgmAttendanceSigners,
} from '../../agm-ufs/composables/agmUfsAttendanceSigners';
import type { AgmUfsFormModel } from '../../agm-ufs/composables/useFormPayload';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** UFS signer helpers — 동일 필드 구조 */
function asUfsSignerModel(m: AgmAfsFormModel): AgmUfsFormModel {
  return m as unknown as AgmUfsFormModel;
}

export function buildAgmAfsAttendanceSignerVariables(m: AgmAfsFormModel) {
  return buildAgmUfsAttendanceSignerVariables(asUfsSignerModel(m));
}

export function buildAgmAfsConsentSignerVariables(m: AgmAfsFormModel) {
  return buildAgmUfsConsentSignerVariables(asUfsSignerModel(m));
}

export function buildAgmAfsMinutesMultipleChairmanSignerVariables(m: AgmAfsFormModel) {
  return buildAgmUfsMinutesMultipleChairmanSignerVariables(asUfsSignerModel(m));
}

export function buildAgmAfsMinutesSoleCorporateSignerVariables(m: AgmAfsFormModel) {
  return buildAgmUfsMinutesSoleCorporateSignerVariables(asUfsSignerModel(m));
}

export function buildAgmAfsMinutesSoleIndividualSignerVariables(m: AgmAfsFormModel) {
  return buildAgmUfsMinutesSoleIndividualSignerVariables(asUfsSignerModel(m));
}

/** Notice of AGM — 이사 1명 (지정 서명자 또는 entityDirectors 첫 명) */
export function buildAgmAfsNoticeDirectorSignerVariables(
  m: AgmAfsFormModel,
): { signers: string; Signers: string } | null {
  let name = '';
  if (m.specifySigners && m.signers?.length) {
    name = trim(m.signers[0]?.name);
  }
  if (!name) {
    name = trim(m.entityDirectors[0]?.name);
  }
  if (!name) return null;
  const list = dedupePresetSigners([{ name, email: '', role: 'director' }], {
    chairmanName: trim(m.chairmanName),
  });
  if (!list.length) return null;
  const json = JSON.stringify(list);
  return { signers: json, Signers: json };
}

/** DR — specify_signers=false 일 때 이사 전원 */
export function buildAgmAfsAllDirectorsSignerVariables(
  m: AgmAfsFormModel,
): { signers: string; Signers: string } | null {
  const rows: AgmEntityPersonRow[] = (m.entityDirectors || []).filter((d) => trim(d.name));
  if (!rows.length && m.signers?.length) {
    for (const s of m.signers) {
      if (trim(s.name)) rows.push({ name: trim(s.name), role: 'director' });
    }
  }
  const list = dedupeAgmAttendanceSigners(rows, { chairmanName: trim(m.chairmanName) });
  if (!list.length) return null;
  const json = JSON.stringify(list.map((s) => ({ ...s, role: 'director' })));
  return { signers: json, Signers: json };
}

export { collectAgmConsentSignerRows, collectAgmShareholderSignerRows };
